/* eslint-disable no-console, max-lines-per-function, @smarttools/rxjs/finnish */
import { Injectable } from '@angular/core';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { defer, from, of, throwError, Subject, race, timer, type Observable, type Subscription } from 'rxjs';
import { catchError, take, switchMap, tap, map, retry } from 'rxjs/operators';

import { CANNES_BLE_CONFIG } from '../interfaces/ble.interface';

/**
 * REST-over-BLE proxy for ZEISS Cannes devices.
 *
 * Flow per request: enable STATUS_CODE notifications -> write URI -> (PUT body) -> trigger ->
 * wait for status notification -> read RESPONSE_BODY.
 *
 * TODO: Refactor to a single reactive pipeline.
 *   - Bridge BleClient.startNotifications callback into a Subject<DataView> once per device.
 *   - Compose executeRequest$ as one chain: write URI -> (body) -> trigger -> notification$.pipe(take(1)) -> read body.
 */
@Injectable({
  providedIn: 'root',
})
export class BleRestProxyService {
  private readonly textEncoder = new TextEncoder();
  private readonly textDecoder = new TextDecoder('utf-8');
  private readonly REQUEST_TIMEOUT_MS = 5_000;
  private readonly RETRY_COUNT = 2;
  private readonly RETRY_DELAY_MS = 0;

  // Resolved REST-proxy service UUID per device (firmware-dependent: 4f311823 vs legacy 4f310823).
  private readonly serviceUuidByDevice = new Map<string, string>();

  private readonly notificationStates = new Map<string, boolean>();
  private readonly activeResponseSubjects = new Map<string, Subject<unknown>>();
  private readonly activeRequestSubscriptions = new Map<string, Subscription>();
  private readonly activeResponseBodySubscriptions = new Map<string, Subscription>();

  /**
   * Discovers which GATT service exposes the REST-proxy characteristics on this device and caches it.
   * Supports firmware that moved the characteristics from 4f310823 to 4f311823.
   * Falls back to the candidate priority list, then to the default service UUID.
   */
  resolveServiceUuid$(deviceId: string): Observable<string> {
    return from(BleClient.getServices(deviceId)).pipe(
      map(services => {
        const uriChar = CANNES_BLE_CONFIG.CHARACTERISTICS.URI_ENDPOINT.toLowerCase();

        const serviceWithUri = services.find(service => service.characteristics?.some(characteristic => characteristic.uuid.toLowerCase() === uriChar));
        if (serviceWithUri) {
          return serviceWithUri.uuid.toLowerCase();
        }

        const discoveredUuids = new Set(services.map(service => service.uuid.toLowerCase()));
        const candidate = CANNES_BLE_CONFIG.REST_SERVICE_CANDIDATES.find(uuid => discoveredUuids.has(uuid.toLowerCase()));
        return candidate ?? CANNES_BLE_CONFIG.SERVICES.ZEISS_CUSTOM;
      }),
      catchError((error: unknown) => {
        console.warn('[BLE REST Proxy] Service resolution failed - using default service UUID:', error);
        return of(CANNES_BLE_CONFIG.SERVICES.ZEISS_CUSTOM);
      }),
      tap(serviceUuid => {
        this.serviceUuidByDevice.set(deviceId, serviceUuid);
        console.log('[BLE REST Proxy] Resolved REST service for device', deviceId, '->', serviceUuid);
      })
    );
  }

  getServiceUuid(deviceId: string): string {
    return this.serviceUuid(deviceId);
  }

  private serviceUuid(deviceId: string): string {
    return this.serviceUuidByDevice.get(deviceId) ?? CANNES_BLE_CONFIG.SERVICES.ZEISS_CUSTOM;
  }

  get$<T>(deviceId: string, endpoint: string): Observable<T> {
    console.log('[BLE REST Proxy] GET request to:', endpoint);
    return defer(() => this.executeRequest$<T>(deviceId, 'GET', endpoint)).pipe(
      retry({
        count: this.RETRY_COUNT,
        delay: (error: unknown, retryIndex: number) => this.recoverBeforeRetry$(deviceId, error, retryIndex),
      })
    );
  }

  put$<T>(deviceId: string, endpoint: string, body?: unknown): Observable<T> {
    // Redact secrets (e.g. the AP passphrase) from the body log — it otherwise leaks into logcat.
    const loggedBody = /passphrase|password/i.test(endpoint) ? '<redacted>' : body;
    console.log('[BLE REST Proxy] PUT request to:', endpoint, 'body:', loggedBody);
    return defer(() => this.executeRequest$<T>(deviceId, 'PUT', endpoint, body));
  }

  ensureNotificationsSetup$(deviceId: string): Observable<void> {
    return this.enableStatusNotifications$(deviceId);
  }

  private executeRequest$<T>(deviceId: string, method: 'GET' | 'PUT', endpoint: string, body?: unknown): Observable<T> {
    const responseSubject = new Subject<T>();

    const requestSubscription = this.enableStatusNotifications$(deviceId, responseSubject)
      .pipe(
        tap(() => console.log('[BLE REST Proxy] Notifications ready')),
        switchMap(() => this.writeUri$(deviceId, endpoint)),
        tap(() => console.log('[BLE REST Proxy] URI written')),
        switchMap(() => (method === 'PUT' && body !== undefined ? this.writeRequestBody$(deviceId, body) : from(Promise.resolve()))),
        tap(() => console.log('[BLE REST Proxy] Body written (if PUT)')),
        switchMap(() =>
          this.triggerRequest$(deviceId, method).pipe(
            catchError((error: unknown) => {
              console.warn('[BLE REST Proxy] Trigger write failed (expected race condition):', error);
              return from(Promise.resolve());
            })
          )
        ),
        tap(() => console.log('[BLE REST Proxy] Request triggered - waiting for notification...'))
      )
      .subscribe({
        error: (error: unknown) => {
          console.error('[BLE REST Proxy] Request setup failed:', error);
          this.activeRequestSubscriptions.delete(deviceId);
          this.activeResponseSubjects.delete(deviceId);
          responseSubject.error(error);
        },
        complete: () => {
          this.activeRequestSubscriptions.delete(deviceId);
        },
      });

    this.activeRequestSubscriptions.set(deviceId, requestSubscription);

    return this.createResponseWithTimeout$<T>(responseSubject);
  }

  private writeUri$(deviceId: string, endpoint: string): Observable<void> {
    const uriData = this.stringToDataView(endpoint);
    return from(BleClient.write(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.URI_ENDPOINT, uriData));
  }

  private writeRequestBody$(deviceId: string, body: unknown): Observable<void> {
    const bodyText = typeof body === 'string' ? body : JSON.stringify(body);
    const bodyData = this.stringToDataView(bodyText);
    return from(BleClient.write(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.RESPONSE_BODY, bodyData));
  }

  private enableStatusNotifications$<T>(deviceId: string, responseSubject?: Subject<T>): Observable<void> {
    if (responseSubject) {
      this.activeResponseSubjects.set(deviceId, responseSubject as Subject<unknown>);
    }

    if (this.notificationStates.get(deviceId)) {
      console.log('[BLE REST Proxy] Notifications already active - reusing existing handler');
      return from(Promise.resolve());
    }

    console.log('[BLE REST Proxy] Setting up notifications for first time');
    return from(
      BleClient.startNotifications(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.STATUS_CODE, (dataView: DataView) => {
        const currentSubject = this.activeResponseSubjects.get(deviceId);
        if (currentSubject) {
          this.handleStatusNotification(deviceId, dataView, currentSubject);
        } else {
          console.warn('[BLE REST Proxy] Received notification but no active request for device:', deviceId);
        }
      })
    ).pipe(
      tap(() => {
        this.notificationStates.set(deviceId, true);
        console.log('[BLE REST Proxy] Notifications enabled and will remain active');
      })
    );
  }

  private triggerRequest$(deviceId: string, method: 'GET' | 'PUT'): Observable<void> {
    const triggerByte = method === 'GET' ? CANNES_BLE_CONFIG.TRIGGERS.GET : CANNES_BLE_CONFIG.TRIGGERS.PUT;
    const triggerData = new DataView(new ArrayBuffer(1));
    triggerData.setUint8(0, triggerByte);

    console.log(`[BLE REST Proxy] Triggering ${method} request with byte: 0x${triggerByte.toString(16)}`);
    return from(BleClient.write(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.CONTROL_POINT, triggerData));
  }

  private handleStatusNotification<T>(deviceId: string, value: DataView, responseSubject: Subject<T>): void {
    if (value.byteLength === 0) {
      // Device sends empty notification for PUT requests as implicit HTTP 200 OK (fire & forget).
      console.log('[BLE REST Proxy] Empty notification received - treating as PUT success');
      responseSubject.next(undefined as T);
      responseSubject.complete();
      this.activeResponseSubjects.delete(deviceId);
      return;
    }

    if (value.byteLength < 3) {
      console.error('[BLE REST Proxy] Invalid status notification length:', value.byteLength);
      responseSubject.error(new Error('Invalid status notification'));
      return;
    }

    const statusLow = value.getUint8(0);
    const statusHigh = value.getUint8(1);
    const statusCode = statusLow | (statusHigh << 8);

    console.log(
      `[BLE REST Proxy] Status notification received: HTTP ${statusCode} (raw: ${Array.from(new Uint8Array(value.buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join(' ')})`
    );

    if (statusCode >= 200 && statusCode < 300) {
      console.log('[BLE REST Proxy] Success status - reading response body');
      const readSubscription = this.readResponseBody$<T>(deviceId).subscribe({
        next: response => {
          console.log('[BLE REST Proxy] Response received:', response);
          this.activeResponseSubjects.delete(deviceId);
          this.activeResponseBodySubscriptions.delete(deviceId);
          responseSubject.next(response);
          responseSubject.complete();
        },
        error: (error: unknown) => {
          console.error('[BLE REST Proxy] Failed to read response body:', error);
          this.activeResponseSubjects.delete(deviceId);
          this.activeResponseBodySubscriptions.delete(deviceId);
          responseSubject.error(error);
        },
      });
      this.activeResponseBodySubscriptions.set(deviceId, readSubscription);
    } else {
      console.error(`[BLE REST Proxy] Request failed with status ${statusCode}`);
      responseSubject.error(new Error(`HTTP ${statusCode}`));
      this.activeResponseSubjects.delete(deviceId);
    }
  }

  private readResponseBody$<T>(deviceId: string): Observable<T> {
    return from(BleClient.read(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.RESPONSE_BODY)).pipe(
      map(dataView => {
        const responseText = this.textDecoder.decode(dataView.buffer);
        console.log('[BLE REST Proxy] Raw response text:', responseText);

        try {
          return JSON.parse(responseText) as T;
        } catch {
          return responseText as T;
        }
      })
    );
  }

  private stopNotifications(deviceId: string): void {
    BleClient.stopNotifications(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.STATUS_CODE).catch(error =>
      console.error('[BLE REST Proxy] Failed to stop notifications:', error)
    );
  }

  private createResponseWithTimeout$<T>(responseSubject: Subject<T>): Observable<T> {
    const timeout = timer(this.REQUEST_TIMEOUT_MS).pipe(switchMap(() => throwError(() => new Error('Request timeout'))));

    return race(responseSubject.asObservable(), timeout).pipe(
      take(1),
      catchError((error: unknown) => {
        console.error('[BLE REST Proxy] Request failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          error: `REST request failed: ${errorMessage}`,
        }));
      })
    );
  }

  private stringToDataView(text: string): DataView {
    const bytes = this.textEncoder.encode(text);
    return new DataView(bytes.buffer);
  }

  resetDeviceNotificationState(deviceId: string): void {
    const notificationState = this.notificationStates.get(deviceId);
    if (notificationState) {
      this.stopNotifications(deviceId);
    }
    this.notificationStates.delete(deviceId);

    const requestSubscription = this.activeRequestSubscriptions.get(deviceId);
    if (requestSubscription) {
      requestSubscription.unsubscribe();
      this.activeRequestSubscriptions.delete(deviceId);
    }

    const activeSubject = this.activeResponseSubjects.get(deviceId);
    if (activeSubject && !activeSubject.closed) {
      activeSubject.error(new Error('Device disconnected during bonding'));
    }
    this.activeResponseSubjects.delete(deviceId);

    const readSubscription = this.activeResponseBodySubscriptions.get(deviceId);
    if (readSubscription) {
      readSubscription.unsubscribe();
      this.activeResponseBodySubscriptions.delete(deviceId);
    }
  }

  private recoverBeforeRetry$(deviceId: string, error: unknown, retryIndex: number): Observable<unknown> {
    console.warn(`[BLE REST Proxy] Request failed (retry ${retryIndex}/${this.RETRY_COUNT}) - attempting recovery:`, error);

    return from(BleClient.getConnectedDevices([this.serviceUuid(deviceId)])).pipe(
      switchMap(devices => {
        const stillConnected = devices.some(d => d.deviceId === deviceId);
        if (!stillConnected) {
          console.error('[BLE REST Proxy] Device not in getConnectedDevices - cannot recover, surfacing error');
          return throwError(() => new Error('Device disconnected'));
        }
        console.log('[BLE REST Proxy] Device still connected - resetting notification subscription (possible handle/CCCD drift)');
        return this.resetNotificationSubscription$(deviceId).pipe(switchMap(() => timer(this.RETRY_DELAY_MS)));
      })
    );
  }

  private resetNotificationSubscription$(deviceId: string): Observable<void> {
    const hadNotifications = this.notificationStates.get(deviceId) === true;

    const stop$: Observable<void> = hadNotifications
      ? from(BleClient.stopNotifications(deviceId, this.serviceUuid(deviceId), CANNES_BLE_CONFIG.CHARACTERISTICS.STATUS_CODE)).pipe(
          catchError((error: unknown) => {
            console.warn('[BLE REST Proxy] stopNotifications during recovery failed (continuing):', error);
            return of(undefined);
          }),
          map(() => void 0)
        )
      : of(void 0);

    return stop$.pipe(
      tap(() => {
        this.notificationStates.delete(deviceId);

        const staleSubject = this.activeResponseSubjects.get(deviceId);
        if (staleSubject && !staleSubject.closed) {
          staleSubject.complete();
        }
        this.activeResponseSubjects.delete(deviceId);

        const staleSubscription = this.activeRequestSubscriptions.get(deviceId);
        if (staleSubscription) {
          staleSubscription.unsubscribe();
          this.activeRequestSubscriptions.delete(deviceId);
        }

        console.log('[BLE REST Proxy] Notification subscription state cleared - will re-subscribe on next request');
      })
    );
  }

  cleanupDevice(deviceId: string): void {
    console.log('[BLE REST Proxy] Cleaning up device state:', deviceId);

    const notificationState = this.notificationStates.get(deviceId);
    if (notificationState) {
      console.log('[BLE REST Proxy] Stopping notifications for device:', deviceId);
      this.stopNotifications(deviceId);
    }

    this.notificationStates.delete(deviceId);

    const requestSubscription = this.activeRequestSubscriptions.get(deviceId);
    if (requestSubscription) {
      requestSubscription.unsubscribe();
      this.activeRequestSubscriptions.delete(deviceId);
    }

    const activeSubject = this.activeResponseSubjects.get(deviceId);
    if (activeSubject && !activeSubject.closed) {
      activeSubject.error(new Error('Device disconnected'));
    }
    this.activeResponseSubjects.delete(deviceId);

    const readSubscription = this.activeResponseBodySubscriptions.get(deviceId);
    if (readSubscription) {
      readSubscription.unsubscribe();
      this.activeResponseBodySubscriptions.delete(deviceId);
    }

    this.serviceUuidByDevice.delete(deviceId);
  }
}
/* eslint-enable no-console, max-lines-per-function, @smarttools/rxjs/finnish */
